if (!customElements.get('sol-purchase-options')) {
  customElements.define(
    'sol-purchase-options',
    class SolPurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-selling-plan-input]');
        try {
          this.variants = JSON.parse(this.querySelector('[data-sol-variants]').textContent);
        } catch (e) {
          this.variants = {};
        }
        this.isBox = this.hasAttribute('data-box');
        if (this.isBox) {
          this.initBox();
          return;
        }
        this.addEventListener('change', (event) => {
          if (event.target.type === 'radio' && this.input) this.input.value = event.target.value;
        });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data && event.data.variant;
            if (variant) this.render(variant.id);
          });
        }
      }
      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.onWindowSubmit) window.removeEventListener('submit', this.onWindowSubmit, true);
      }
      render(variantId) {
        const data = this.variants[variantId];
        if (!data) return;
        this.querySelectorAll('[data-onetime-price]').forEach((el) => (el.textContent = data.price));
        Object.entries(data.plans).forEach(([planId, plan]) => {
          const set = (attr, value) => this.querySelectorAll(`[${attr}="${planId}"]`).forEach((el) => (el.textContent = value));
          set('data-plan-price', plan.price);
          set('data-plan-compare', plan.compare);
          set('data-plan-perserving', plan.perserving);
          this.querySelectorAll(`[data-plan-save="${planId}"]`).forEach((el) => {
            el.textContent = el.textContent.replace(/[^\s]+$/, plan.save);
          });
        });
      }

      /* ---------- Strength & Sleep Box ----------
       * The box is a Shopify Bundles product (no selling plans). Subscribe adds the components with their
       * 7-week plans; One-time adds the bundle itself (or the components without a plan if it's unavailable).
       * All IDs come from data attributes rendered by snippets/sol-purchase-options.liquid. */
      initBox() {
        const d = this.dataset;
        this.form = document.getElementById(d.formId);
        if (d.subscribeAvailable !== 'true') {
          console.warn(`[Solmetta] Box subscribe option hidden: ${d.subscribeIssue || 'subscription plans not found'}.`);
        }
        if (!this.form) {
          console.warn('[Solmetta] Box purchase options: product form not found.');
          return;
        }
        this.productForm = this.form.closest('product-form');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-box-mode]')) this.updateButton();
        });
        // Capture on window runs before product-form's own submit handler, so the default
        // single-variant add never fires for the box.
        this.onWindowSubmit = (event) => {
          if (event.target !== this.form) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addBox();
        };
        window.addEventListener('submit', this.onWindowSubmit, true);
        this.updateButton();
        // product-form's constructor may run after us and reset the button; re-apply once it's defined.
        if (window.customElements && customElements.whenDefined) {
          customElements.whenDefined('product-form').then(() => this.updateButton());
        }
      }
      selectedMode() {
        const checked = this.querySelector('[data-box-mode]:checked');
        return checked ? checked.value : 'onetime';
      }
      modeAvailable(mode) {
        return mode === 'subscribe' ? this.dataset.subscribeAvailable === 'true' : this.dataset.onetimeAvailable === 'true';
      }
      updateButton() {
        if (!this.submitButton) return;
        const available = this.modeAvailable(this.selectedMode());
        const label = this.submitButton.querySelector('span');
        if (available) {
          this.submitButton.removeAttribute('disabled');
          if (label && this.dataset.labelAdd) label.textContent = this.dataset.labelAdd;
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (label && this.dataset.labelSoldout) label.textContent = this.dataset.labelSoldout;
        }
      }
      buildItems(mode) {
        const d = this.dataset;
        const props = { _bundle: d.bundleName || 'Strength & Sleep Box' };
        const magQty = parseInt(d.magnesiumQty, 10) || 2;
        if (mode === 'subscribe') {
          if (d.subscribeAvailable !== 'true' || !d.creatinePlan || !d.magnesiumPlan) return null;
          return [
            { id: Number(d.creatineVariant), quantity: 1, selling_plan: Number(d.creatinePlan), properties: props },
            { id: Number(d.magnesiumVariant), quantity: magQty, selling_plan: Number(d.magnesiumPlan), properties: props },
          ];
        }
        if (d.boxVariant) return [{ id: Number(d.boxVariant), quantity: 1 }];
        if (d.fallbackAvailable === 'true' && d.creatineVariant && d.magnesiumVariant) {
          return [
            { id: Number(d.creatineVariant), quantity: 1, properties: props },
            { id: Number(d.magnesiumVariant), quantity: magQty, properties: props },
          ];
        }
        return null;
      }
      showError(message) {
        const wrapper = this.productForm && this.productForm.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) {
          if (message) console.error(`[Solmetta] ${message}`);
          return;
        }
        const text = wrapper.querySelector('.product-form__error-message');
        wrapper.toggleAttribute('hidden', !message);
        if (text) text.textContent = message || '';
      }
      setLoading(loading) {
        if (!this.submitButton) return;
        const spinner = this.submitButton.querySelector('.loading__spinner');
        this.submitButton.classList.toggle('loading', loading);
        if (spinner) spinner.classList.toggle('hidden', !loading);
        if (loading) this.submitButton.setAttribute('aria-disabled', 'true');
        else this.submitButton.removeAttribute('aria-disabled');
      }
      addBox() {
        if (this.busy) return;
        const mode = this.selectedMode();
        const items = this.buildItems(mode);
        if (!items) {
          console.warn(`[Solmetta] Box: "${mode}" is not available right now.`);
          this.updateButton();
          return;
        }
        this.busy = true;
        this.showError(false);
        this.setLoading(true);

        // Open the theme's cart drawer when there is one; otherwise (page/notification cart) go to /cart.
        const cart = document.querySelector('cart-drawer');
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }
        const url = this.dataset.cartAddUrl || '/cart/add';
        const cartUrl = this.dataset.cartUrl || '/cart';

        fetch(`${url.replace(/\.js$/, '')}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
          .then(({ ok, json }) => {
            if (!ok || json.status) {
              const message = json.description || json.message || 'Could not add the box to your cart.';
              this.showError(message);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, { source: 'sol-box', errors: json.errors || message, message: json.message });
              }
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sol-box', cartData: json });
            }
            if (cart && json.sections && typeof cart.renderContents === 'function') {
              try {
                cart.renderContents(json);
                cart.classList.remove('is-empty');
                return;
              } catch (e) {
                console.error(e);
              }
            }
            window.location = cartUrl;
          })
          .catch((e) => {
            console.error(e);
            this.showError('Network error. Please try again.');
          })
          .finally(() => {
            this.busy = false;
            this.setLoading(false);
          });
      }
    }
  );
}
